/**
 * HearingOutcomeHost — the hearing card, opened from the permit tracker instead of the daily triage
 * (Matt, 2026-10-26: triage is off for most people). An agency review's ⋯ "Record the hearing outcome…",
 * and the Log Activity window when an approval or continuance is being logged, open this. Same card as
 * the triage (HearingOutcomeCard.jsx#hearingOptions), same commit, so the permit gets one entry either
 * way. docs/HEARING_OUTCOMES.md "One entry either way".
 *
 * Opens on `open-hearing-outcome` { reviewId }. Mounted once in OutlineView.
 */

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import TriageTiles from './TriageTiles.jsx'
import { hearingOptions } from './HearingOutcomeCard.jsx'
import { getHearingCardForReview } from '../services/passengers.js'
import { ctx } from '../outline/ctx.js'
import { wf } from '../outline/state.js'

export default function HearingOutcomeHost() {
  const [reviewId, setReviewId] = useState(null)
  const [state, setState] = useState(null)   // null loading | { card } | { none } | { error }
  useEffect(() => {
    const on = (e) => {
      const id = e.detail && e.detail.reviewId
      if (!id) return
      setReviewId(id); setState(null)
      getHearingCardForReview(id).then(setState).catch(err => setState({ error: err.message || 'Could not load the hearing' }))
    }
    window.addEventListener('open-hearing-outcome', on)
    return () => window.removeEventListener('open-hearing-outcome', on)
  }, [])
  if (!reviewId) return null

  const f = (ctx.currentMember && ctx.currentMember.fields) || {}
  const me = { id: wf.currentMemberId, name: f['Member Name'] || f['Name'] || '', email: f['Member Email'] || '' }
  const close = () => { setReviewId(null); setState(null); window.dispatchEvent(new Event('outline-render')) }
  const card = state && state.card
  const opts = card ? hearingOptions(card, me) : []

  return createPortal(
    <div onClick={close} style={{ position: 'fixed', inset: 0, zIndex: 700, background: 'rgba(7,39,45,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '48px 16px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 'min(760px, 100%)', background: 'var(--aj-bg-surface, #fff)', borderRadius: 10, padding: '20px 24px', boxShadow: '0 20px 60px rgba(7,39,45,0.3)', fontFamily: '"DM Sans",sans-serif', color: 'var(--aj-text, #07272D)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: '"Montserrat",sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text-muted, #7a9aaa)' }}>Hearing: what did the board decide?</div>
            {card && <div style={{ fontSize: 15, fontWeight: 600, marginTop: 4 }}>{card.projectName} · hearing {new Date(card.hearingDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>}
            {card && <div style={{ fontSize: 12, color: 'var(--aj-text-muted, #7a9aaa)', marginTop: 2 }}>{card.agencyName}{card.trackingNumber ? ' · ' + card.trackingNumber : ''}{card.logged ? ` · already logged as ${card.logged}, it stays as it is` : ''}</div>}
          </div>
          <button onClick={close} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 22, lineHeight: 1, cursor: 'pointer', color: 'var(--aj-text-muted, #7a9aaa)' }}>×</button>
        </div>
        {!state && <div style={{ fontSize: 13, color: 'var(--aj-text-muted, #7a9aaa)' }}>Loading the hearing…</div>}
        {state && state.error && <div style={{ fontSize: 13, color: '#c0392b' }}>{state.error}</div>}
        {state && state.none && <div style={{ fontSize: 13 }}>{state.none}</div>}
        {card && (
          <TriageTiles options={opts}
            onConfirm={async (key, v) => { const o = opts.find(x => x.key === key); const r = await o.run(v); return o.done(r) }}
            onFolded={close} />
        )}
      </div>
    </div>,
    document.body
  )
}
